import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ContactGroupType } from 'contacts/enums';
import { Steps } from 'core/components';
import { StepsProvider } from 'core/states';
import { IStep } from 'core/types';
import { Color, Size } from 'theme/enums';

import { useAddContactGroupByEmailContext } from './state';
import { ContactEmail, ContactGroupDetails, Contacts, Locations } from './Steps';

interface IProps {
  isOpen: boolean;
  level?: number;
}

export const AddContactGroupByEmailSteps = ({ isOpen, level }: IProps) => {
  const { t } = useTranslation();

  const {
    contactGroup,
    contactGroupType,
    onContactEmailSubmit,
    onContactGroupSubmit,
    onSearchCustomerSubmit,
    onClose,
  } = useAddContactGroupByEmailContext();

  const title = useMemo(() => {
    return contactGroupType === ContactGroupType.CUSTOMER
      ? t('titleAddCustomer')
      : t('titleAddVendor');
  }, [contactGroupType, t]);

  const steps: IStep[] = useMemo(
    () => [
      {
        type: 'contactEmail',
        title: t('titleContactEmail'),
        content: <ContactEmail />,
        nextButton: {
          options: {
            onClick: onContactEmailSubmit,
          },
        },
      },
      {
        type: 'contactGroupDetails',
        title: t('titleContactGroupDetails'),
        content: <ContactGroupDetails />,
        nextButton: {
          options: {
            onClick: onContactGroupSubmit,
          },
        },
      },
      {
        type: 'contactGroupLocations',
        title: t('titleLocations'),
        subtitle: contactGroup?.name,
        content: <Locations level={level} />,
      },
      {
        type: 'contactGroupContacts',
        title: t('titleContacts'),
        subtitle: contactGroup?.name,
        content: <Contacts level={level} />,
        nextButton: {
          text: t('buttonLabelDone'),
          options: {
            onClick: onSearchCustomerSubmit,
          },
        },
      },
    ],
    [
      t,
      onContactEmailSubmit,
      onContactGroupSubmit,
      contactGroup?.name,
      level,
      onSearchCustomerSubmit,
    ]
  );

  return (
    <StepsProvider steps={steps} onClose={onClose}>
      <Steps
        title={title}
        isOpen={isOpen}
        color={Color.NEUTRAL}
        size={Size.MD}
        fullWidth={false}
        level={level}
      />
    </StepsProvider>
  );
};
